import { createContext, useContext } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import { Auth } from "aws-amplify";
import { query } from "../services/salesforce";
import { get } from "../api";
import { init, FullStory } from "@fullstory/browser";
init({ orgId: process.env.REACT_APP_FULL_STORY_ORG_ID });

const AuthContext = createContext();

const randomDigits = (number) => {
    const randomValues = new Uint8Array(number);
    window.crypto.getRandomValues(randomValues);
    return Array.from(randomValues).map(intToHex).join("");
};

const intToHex = (number) => {
    return number.toString(16).padStart(2, "0");
};

const getAllUrlParams = (url) => {
    let queryString = url ? url.split("?")[1] : window.location.search.slice(1);

    let obj = {};

    if (queryString) {
        queryString = queryString.split("#")[0];

        let arr = queryString.split("&");

        for (let i = 0; i < arr.length; i++) {
            let a = arr[i].split("=");

            let paramName = a[0];
            let paramValue = typeof a[1] === "undefined" ? true : a[1];

            if (paramName.match(/\[(\d+)?\]$/)) {
                let key = paramName.replace(/\[(\d+)?\]/, "");
                if (!obj[key]) obj[key] = [];

                if (paramName.match(/\[\d+\]$/)) {
                    let index = /\[(\d+)\]/.exec(paramName)[1];
                    obj[key][index] = paramValue;
                } else {
                    obj[key].push(paramValue);
                }
            } else {
                if (!obj[paramName]) {
                    obj[paramName] = paramValue;
                } else if (obj[paramName] && typeof obj[paramName] === "string") {
                    obj[paramName] = [obj[paramName]];
                    obj[paramName].push(paramValue);
                } else {
                    obj[paramName].push(paramValue);
                }
            }
        }
    }

    return obj;
};

export const AuthProvider = ({ children, application }) => {
    const [user, setUser] = useLocalStorage("user", null);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const redirectLocation = location.state?.path || "/";
    const answerSearchParams = location.state?.searchParams;

    const passwordLessLogin = async (data) => {
        let paramsData = redirectLocation ? getAllUrlParams(redirectLocation) : {};
        let type = paramsData.type ? paramsData.type : "";
        let recordId = paramsData.recordId ? paramsData.recordId : "";
        let email = data.email.toLowerCase();
        const params = {
            username: email,
            password: data.password ? data.password : randomDigits(10),
            attributes: {
                name: email,
                email: email,
            },
            validationData: { type: type ? type : "" },
        };
        let hasError = false;
        try {
            await Auth.signUp(params);
        } catch (e) {
            if (e.message.indexOf("Email not found") > -1) {
                hasError = true;
            }
            console.log(e);
        }
        if (!hasError) {
            try {
                const thisUser = await Auth.signIn(email);
                setUser(thisUser);
                let existingSearchParmas = location?.state?.searchParams ? location?.state?.searchParams : {};
                navigate("/answer", {
                    replace: true,
                    state: {
                        path: redirectLocation,
                        searchParams: {
                            simulation: searchParams.get("simulation"),
                            type: type,
                            recordId: recordId,
                            ...existingSearchParmas,
                        },
                    },
                });
            } catch (e) {
                console.log(e);
                setTimeout(() => window.location.reload(), 2000);
            }
        } else {
            if (data.callback) {
                data.callback(true);
            }
        }
    };

    const login = async (data) => {
        try {
            const thisUser = await Auth.signIn(data.email, data.password);
            setUser(thisUser);
            navigate(redirectLocation, { replace: true });
        } catch (e) {
            console.log(e);
            setTimeout(() => window.location.reload(), 2000);
        }
    };

    const answer = async (userInfo, answer, callback) => {
        try {
            let user = userInfo;
            if (user.challengeName === "CUSTOM_CHALLENGE") {
                user = await Auth.sendCustomChallengeAnswer(user, answer);
                await Auth.currentSession();
                let userData = await Auth.currentAuthenticatedUser();
                setUser(userData);
                await getUserDetails();
                let userDetails = getUserDetailsNotAsync();
                let firstName = userDetails.applicant?.contact?.firstName
                    ? userDetails.applicant?.contact?.firstName
                    : userDetails.applicant.firstName;
                let lastName = userDetails.applicant?.contact?.lastName
                    ? userDetails.applicant?.contact?.lastName
                    : userDetails.applicant.lastName;

                FullStory("setProperties", {
                    type: "user",
                    properties: {
                        displayName: firstName + " " + lastName,
                        email: userDetails.username,
                    },
                });
                window.Intercom("boot", {
                    app_id: "bx65r6t2",
                    name: firstName + " " + lastName,
                    email: userDetails.username,
                    user_id: userDetails.username,
                });

                if (redirectLocation?.startsWith("/apply-now")) {
                    navigate(redirectLocation, { replace: true });
                } else if (userDetails?.applicants?.length > 1) {
                    navigate("/multiple-application", {
                        replace: true,
                    });
                } else {
                    if (userDetails?.applicant?.status === "Submitted") {
                        navigate("/application-completion", {
                            replace: true,
                        });
                    } else {
                        navigate("/", { replace: true });
                    }
                }
            }
        } catch (e) {
            console.log(e);
            if (e === "No current user") {
                callback("Not a valid code. Please try again.");
            } else {
                callback("Not a valid user session. Please try again.");
                if (e.message) {
                    setTimeout(() => {
                        navigate("/signout", {
                            replace: true,
                            state: {
                                path: redirectLocation,
                                searchParams: { simulation: searchParams.get("simulation") },
                            },
                        });
                    }, 4 * 1000);
                }
            }
        }
    };

    const logout = async () => {
        await Auth.signOut();
        setUser(null);
        localStorage.clear();
        navigate("/login" + (location.state?.query ? location.state?.query : ""), {
            replace: true,
            state: { path: location.state?.path },
        });
    };

    const logoutWithoutNavigate = async () => {
        await Auth.signOut();
    };

    const isAuthenticated = async () => {
        try {
            let user = await Auth.currentSession();
            return true;
        } catch {
            return false;
        }
    };

    const getUserDetailsNotAsync = () => {
        let jwtToken = user?.signInUserSession?.accessToken?.jwtToken;
        if (jwtToken) {
            const userData = localStorage.getItem(jwtToken);
            if (userData) {
                return JSON.parse(userData);
            }
        }
        return "";
    };

    const saveCustomAttributes = async (data) => {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.updateUserAttributes(user, data);
    };

    const setUserDetailsNotAsync = (details) => {
        let jwtToken = user?.signInUserSession?.accessToken?.jwtToken;
        if (jwtToken) {
            localStorage.setItem(jwtToken, JSON.stringify(details));
        }
        return "";
    };

    const refreshSessionData = async () => {
        let isAuthenticat = await isAuthenticated();
        if (!isAuthenticat) {
            navigate("/signout", {
                replace: true,
            });
            return;
        }
        if (user) {
            let jwtToken = user?.signInUserSession?.accessToken?.jwtToken;
            if (jwtToken) {
                let result;
                let currentUserDetail = getUserDetailsNotAsync();
                result = await get(`applicant/getApplicants`, {
                    emailId: encodeURIComponent(user.username),
                    listingId: currentUserDetail.listingId ? encodeURIComponent(currentUserDetail.listingId) : "",
                    onlyFetch: "yes",
                });
                if (result.isSuccess && result.data && result.data.length > 0) {
                    if (currentUserDetail?.applicant?.id) {
                        user["applicant"] = result.data?.find((item) => item.id === currentUserDetail?.applicant?.id);
                    } else {
                        user["applicant"] = result.data[0];
                    }

                    user["applicants"] = result.data;
                    user["listingId"] = currentUserDetail.listingId;
                    await saveCustomAttributes({ "custom:uniqueId": result.data[0].contactId });
                } else {
                    navigate("/signout", {
                        replace: true,
                    });
                }
                if (answerSearchParams?.type) {
                    user["userType"] = answerSearchParams?.type;
                }
                if (answerSearchParams?.type === "user") {
                    user["loggedInAsUser"] = true;
                }
                user["application"] = application;
                let failed = false;
                try {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                } catch (error) {
                    failed = true;
                    localStorage.clear();
                }

                if (failed) {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                }

                return user;
            }
        }
    };

    const getUserDetails = async () => {
        if (!user) {
            let user = await Auth.currentAuthenticatedUser();
            setUser(user);
        }

        let jwtToken = user.signInUserSession.accessToken.jwtToken;
        if (jwtToken) {
            const userData = localStorage.getItem(jwtToken);
            if (userData) {
                return JSON.parse(userData);
            } else {
                let result;

                result = await get(`applicant/getApplicants`, {
                    emailId: encodeURIComponent(user.username),
                    listingId: answerSearchParams?.recordId ? encodeURIComponent(answerSearchParams.recordId) : "",
                });

                if (result.isSuccess && result.data && result.data.length > 0) {
                    user["applicant"] = result.data[0];
                    user["applicants"] = result.data;
                    user["listingId"] = answerSearchParams?.recordId;
                    await saveCustomAttributes({ "custom:uniqueId": result.data[0].contactId });
                }
                if (answerSearchParams?.type) {
                    user["userType"] = answerSearchParams?.type;
                }
                if (answerSearchParams?.type === "user") {
                    user["loggedInAsUser"] = true;
                }
                user["application"] = application;
                let failed = false;
                try {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                } catch (error) {
                    failed = true;
                    localStorage.clear();
                }

                if (failed) {
                    localStorage.setItem(jwtToken, JSON.stringify(user));
                }

                return user;
            }
        }

        return user;
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated,
                getUserDetails,
                passwordLessLogin,
                login,
                answer,
                logout,
                getUserDetailsNotAsync,
                setUserDetailsNotAsync,
                refreshSessionData,
                logoutWithoutNavigate,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => {
    return useContext(AuthContext);
};

export { useAuth };
